import React from 'react';
import { ClientMilestone } from '@models/client.model';
import { useTranslation } from '@hooks/useTranslation';

interface ClientNextUpProps {
  milestones: ClientMilestone[];
}

/** Milestone dates are server date-only values serialised at UTC midnight. */
const formatMilestoneDate = (iso: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
};

/**
 * Sprint 44, story-226. Up to three upcoming starts/ends, in the order the server gave them
 * (ascending). Task name only, never an assignee. Renders nothing when the server sent none (it
 * sends none for a CLIENT whose schedule dates are OFF), so the web invents no gate of its own.
 */
export const ClientNextUp: React.FC<ClientNextUpProps> = ({ milestones }) => {
  const t = useTranslation('clientProgress');
  if (!milestones || milestones.length === 0) return null;

  return (
    <div data-testid="client-next-up" className="mt-4">
      <h3 className="text-sm font-medium text-gray-900 dark:text-white">
        {t.nextUp}
      </h3>
      <ul className="mt-1 flex flex-col gap-1">
        {milestones.slice(0, 3).map((milestone) => (
          <li
            key={`${milestone.taskId}:${milestone.kind}`}
            className="flex items-baseline gap-3 text-sm">
            <span className="w-14 flex-shrink-0 text-gray-500 dark:text-gray-400">
              {formatMilestoneDate(milestone.date)}
            </span>
            <span className="w-14 flex-shrink-0 text-gray-500 dark:text-gray-400">
              {milestone.kind === 'START' ? t.milestoneStarts : t.milestoneDue}
            </span>
            <span
              className="min-w-0 truncate text-gray-900 dark:text-white"
              title={milestone.name}>
              {milestone.name}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default ClientNextUp;
